/**
 * InviteMemberDialog: add someone who already has an account to the team.
 * Opened from the Members header; one field, Cancel and Invite. The
 * mutation belongs to MembersSection (see ConfirmDialog for why); this is
 * mounted only while open, so every invite starts from an empty field,
 * and it refuses to close mid-flight.
 */

import { useState, type FormEvent } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function InviteMemberDialog({
  isPending,
  error,
  onSubmit,
  onClose,
}: {
  isPending: boolean
  error: string | null
  onSubmit: (email: string) => void
  onClose: () => void
}): React.JSX.Element {
  const [email, setEmail] = useState('')
  const trimmed = email.trim()

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (trimmed === '' || isPending) return
    onSubmit(trimmed)
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite member</DialogTitle>
          <DialogDescription>
            They must already have a TrainLoop account. They get access to every resource on
            this team.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
              placeholder="colleague@company.com"
              autoFocus
              required
              aria-describedby={error === null ? undefined : 'invite-email-error'}
            />
            {error !== null && (
              <p id="invite-email-error" className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || trimmed === ''}>
              {isPending ? 'Inviting...' : 'Invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
